import Stack from "@mui/material/Stack";
import { alpha } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";

interface SystemClockProps {
  isCollapsed: boolean;
}

const formatTime = (date: Date): string =>
  date.toLocaleTimeString("en-GB", {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

const formatDate = (date: Date): string =>
  date
    .toLocaleDateString("en-GB", {
      timeZone: "UTC",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    .replaceAll("/", ".");

export function SystemClock({ isCollapsed }: SystemClockProps) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (isCollapsed) {
      return;
    }

    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [isCollapsed]);

  if (isCollapsed) {
    return null;
  }

  return (
    <Tooltip
      title={"All transaction timestamps\nare displayed in system time."}
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            textAlign: "center",
            mb: 0.5,
          },
        },
      }}
      arrow
    >
      <Stack
        gap={0.5}
        data-testid="system-clock"
        sx={{
          width: "100%",
          borderTop: 1,
          borderColor: (theme) => alpha(theme.palette.common.white, 0.16),
          py: 1.5,
          px: 2.5,
        }}
      >
        <Typography
          variant="body4"
          sx={{ color: (theme) => alpha(theme.palette.common.white, 0.7) }}
        >
          System Time Zone: UTC
        </Typography>
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ fontWeight: 700, color: "common.white" }}
        >
          {formatTime(now)}
        </Typography>
        <Typography
          variant="body4"
          sx={{ color: (theme) => alpha(theme.palette.common.white, 0.7) }}
        >
          {formatDate(now)}
        </Typography>
      </Stack>
    </Tooltip>
  );
}
